<script setup>
import {computed, ref, watch} from 'vue'
import Button from '@/common/Button.vue'
import FormPageContentField from "@/common/form/FormPageContentField.vue"
import api from '@/services/api'
import {usePageSectionsStore} from '@/stores/pageSections'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useRoute, useRouter} from "vue-router"

const props = defineProps({
    contentIndex: Number,
    pageContentLinkId: Number
})

const router = useRouter()
const route = useRoute()

const pageSectionsStore = usePageSectionsStore()
const pageContentDataStore = usePageContentDataStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()

const pageContentData = computed(() => pageContentDataStore.items.find(o => o.id === props.pageContentLinkId))
const contentId = computed(() => pageContentData.value?.contentId)
const contentArea = computed(() => pageContentData.value?.contentArea)
const pageId = computed(() => pageContentData.value?.pageId)
const pageSectionLinkId = computed(() => pageContentData.value?.pageSectionLinkId)
const pageSections = computed(() => (pageSectionsStore.items || []).filter(o => o.pageId === pageId.value))
const isSiteTree = computed(() => route.name === 'siteTree')

const contentAreas = computed(() => {
    return pageSections.value
        .find(o => o.id === pageSectionLinkId.value)?.areas
        ?.map(o => o.value) || []
})

const areaItems = computed(() => {
    return pageContentDataStore.items
        .filter(o => o.pageId === pageId.value && o.contentArea === contentArea.value)
})

const content = ref(null)
watch(
    contentId,
    async (newContentId) => {
        if (!newContentId) {
            content.value = null
            return
        }
        content.value = await itemDataStore.get({tableName: 'content', id: newContentId})
    },
    {immediate: true}
)

const template = ref(null)
watch(
    content,
    async (newContent) => {
        if (!newContent) {
            template.value = null
            return
        }
        template.value = await itemDataStore.get({
            tableName: 'templates_templates',
            id: newContent.contentType
        })
    },
    {immediate: true}
)

const editContent = () => {
    router.push(
        isSiteTree.value
            ? {name: 'siteTree', params: {pageOrContentId: 'c' + props.pageContentLinkId}}
            : {name: 'form', params: {component: 'content', id: contentId.value}}
    )
}

const moveContentUp = async () => {
    const pageContentLinkId = props.pageContentLinkId
    let currentSectionLinkId = pageSectionLinkId.value
    let currentContentArea = contentArea.value
    let displayOrder

    if (props.contentIndex === 0) {
        const areaIndex = contentAreas.value.indexOf(contentArea.value)

        if (areaIndex === 0) {
            const sectionIndex = pageSections.value.findIndex(o => o.id === pageSectionLinkId.value)

            if (sectionIndex === 0) return

            currentSectionLinkId = pageSections.value[sectionIndex - 1]?.id
            currentContentArea = pageSections.value.find(o => o.id === currentSectionLinkId).areas.at(-1).value
        } else {
            currentContentArea = contentAreas.value.at(areaIndex - 1)
        }

        const newAreasItems = pageContentDataStore.items.filter(o => o.pageId === pageId.value && o.contentArea === currentContentArea)
        displayOrder = newAreasItems.length + 1

    } else {
        displayOrder = pageContentData.value.displayOrder - 1
    }

    await api.post('api/move-page-content', {
        pageContentLinkId,
        pageSectionLinkId: currentSectionLinkId,
        contentArea: currentContentArea,
        displayOrder
    })

    await pageContentDataStore.init()
}

const moveContentDown = async () => {
    const pageContentLinkId = props.pageContentLinkId
    let currentSectionLinkId = pageSectionLinkId.value
    let currentContentArea = contentArea.value
    let displayOrder

    const isLastContentInArea = props.contentIndex === areaItems.value.length - 1
    if (isLastContentInArea) {
        const isLastAreaInSection = contentArea.value === contentAreas.value.at(-1)

        if (isLastAreaInSection) {
            const sectionIndex = pageSections.value.findIndex(o => o.id === pageSectionLinkId.value)
            currentSectionLinkId = pageSections.value[sectionIndex + 1]?.id

            if (currentSectionLinkId === undefined) return

            currentContentArea = pageSections.value.find(o => o.id === currentSectionLinkId).areas[0].value

        } else {
            const areaIndex = contentAreas.value.indexOf(contentArea.value)
            currentContentArea = contentAreas.value.at(areaIndex + 1)
        }

        displayOrder = 1

    } else {
        displayOrder = pageContentData.value.displayOrder + 1
    }

    await api.post('api/move-page-content', {
        pageContentLinkId,
        pageSectionLinkId: currentSectionLinkId,
        contentArea: currentContentArea,
        displayOrder
    })

    await pageContentDataStore.init()
}

const deleteContent = () => {
    const num = pageContentDataStore.items.filter(o => o.contentId === contentId.value)?.length

    if (num === 1) {
        modalsStore.show({
            componentName: 'ConfirmModal',
            obj: {
                modalTitle: 'Delete content',
                modalBody: `
                    <p>This is the <strong>only</strong> instance of this content in use.</p>
                    <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                    <p class="mb-0">Are you sure?</p>
                `,
                onConfirm: async () => {
                    try {
                        await itemDataStore.delete({tableName: 'content', id: contentId.value})
                        await pageContentDataStore.init()
                    } catch (err) {
                        // Handle error if needed
                    }
                }
            }
        })
    } else {
        modalsStore.show({
            componentName: 'ConfirmModal',
            obj: {
                modalTitle: 'Detach content',
                modalBody: `
                    <p class="alert alert-warning">No other references to this content will be affected.</p>
                `,
                onConfirm: async () => {
                    try {
                        await api.delete(`api/delete-page-content-link-id/${props.pageContentLinkId}`)
                        await pageContentDataStore.init()
                    } catch (err) {
                        // Handle error if needed
                    }
                }
            }
        })
    }
}
</script>

<template>
    <div class="list-group-item py-1 pe-2 d-flex align-items-center">
        <span class="flex-fill">
            <FormPageContentField :content-id="contentId"/>
        </span>

        <Button @click="editContent" class="btn-sm"><i class="bi bi-pencil-square"/></Button>
        <Button @click="moveContentUp" class="btn-sm"><i class="bi bi-chevron-up"/></Button>
        <Button @click="moveContentDown" class="btn-sm"><i class="bi bi-chevron-down"/></Button>
        <Button @click="deleteContent" class="btn-sm"><i class="bi bi-x-lg"/></Button>
    </div>
</template>